import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  processAction,
  getCaseDetails,
  GetCaseLineItemDetail,
  GetPaymentDetails,
  verifyReceivedCheck,
} from 'src/services/WorkflowService'
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { PatientInvoiceDetailDTO } from 'src/viewModels/workflow/PatientInvoiceDetailDTO'
import { InvoicePaymentSummary } from 'src/viewModels/workflow/InvoicePaymentSummary'
import { PaymentRequest } from 'src/viewModels/PaymentRequest'
import { formatDate } from 'src/utils/Formatter'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'

interface IPatientInvoiceDetailDTOProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function ApplyPaymentModal(props: IPatientInvoiceDetailDTOProps) {
  const [loading, setLoading] = useState(false)
  const [caseDetail, setCaseDetail] = useState<PatientInvoiceDetailDTO | null>(null)
  const [paymentDetails, setPaymentDetails] = useState<InvoicePaymentSummary | null>(null)
  const [lineItems, setLineItems] = useState<any[]>([])
  const [selectedLineItemId, setSelectedLineItemId] = useState<string>('')
  const selectItems = lineItems.map((item) => ({
    ...item,
    disabled: !item.isEnabled,
  }))
  const [isCheckVerified, setIsCheckVerified] = useState(false)
  const [verifyResponse, setVerifyResponse] = useState<string | null>(null)
  const [lastVerifiedValues, setLastVerifiedValues] = useState<any>(null)
  const allLineItemsDisabled =
    lineItems.length > 0 && lineItems.every((item) => !item.isEnabled)
  const formatCurrency = (amount?: number) =>
    amount !== undefined ? `$${amount.toFixed(2)}` : ''
  const [appliedPayments, setAppliedPayments] = useState<any[]>([])
  const [caseLoading, setCaseLoading] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)

  useEffect(() => {
    if (props.isOpen && props.associatedItemId) {
      GetCaseLineItemDetail(Number(props.associatedItemId), true)
        .then((items) => setLineItems(items || []))
        .catch(() => setLineItems([]))
    } else if (!props.isOpen) {
      setPaymentDetails(null)
      setLineItems([])
      setSelectedLineItemId('')
    }
  }, [props.isOpen, props.associatedItemId])

  useEffect(() => {
    if (props.isOpen && selectedLineItemId) {
      setCaseLoading(true)
      getCaseDetails(Number(selectedLineItemId))
        .then((data) => setCaseDetail(data || null))
        .finally(() => setCaseLoading(false))
    } else if (!selectedLineItemId) {
      setCaseDetail(null)
    }
  }, [props.isOpen, selectedLineItemId])

  useEffect(() => {
    if (props.isOpen && selectedLineItemId) {
      GetPaymentDetails(Number(selectedLineItemId))
        .then((details) => setPaymentDetails(details || null))
        .catch(() => setPaymentDetails(null))
    } else if (!selectedLineItemId) {
      setPaymentDetails(null)
    }
  }, [props.isOpen, selectedLineItemId])

  useEffect(() => {
    if (props.isOpen && selectedLineItemId) {
      setAppliedPayments(paymentDetails?.payments || [])
    } else {
      setAppliedPayments([])
    }
  }, [props.isOpen, selectedLineItemId, paymentDetails])

  useEffect(() => {
    if (props.isOpen && selectedLineItemId && caseDetail) {
      if (!caseDetail.invoiceNumber) {
        setInvoiceError(
          'You cannot apply a payment to a line item with no invoice number.'
        )
      } else {
        setInvoiceError(null)
      }
    } else {
      setInvoiceError(null)
    }
  }, [props.isOpen, selectedLineItemId, caseDetail])
  const handlesOnClose = () => {
    props.closeModal()
  }

  async function processApplyPayment(values: any) {
    setLoading(true)
    let data = new ObjectiveProcessAction()
    const ApplyPaymentRequest: PaymentRequest = {
      caseLineItemId: values.selectedLineItemId
        ? Number(values.selectedLineItemId)
        : undefined,
      checkNumber: values.checkNumber || '',
      checkDate: values.checkDate ? new Date(values.checkDate) : undefined,
      checkAmount: values.checkAmount === '' ? 0 : Number(values.checkAmount),
      paymentToApply: values.AmountToApply === '' ? 0 : Number(values.AmountToApply),
      payer: values.defaultPayer || '',
    }
    data.casePaymentRequest = ApplyPaymentRequest
    if (values.comments) {
      data.comment = values.comments
    }
    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish!(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading(false)
        props.closeModal()
      })
  }

  const initialValues = {
    selectedLineItemId: selectedLineItemId || undefined,
    checkDate: '',
    checkNumber: '',
    defaultPayer: '',
    checkAmount: '',
    AmountToApply: '',
    comments: '',
    totalRemainingFunds: 0,
    checkId: '',
    totalFundsApplied: 0,
    refund: false,
    requireComment: props.action.requireComment,
  }

  const isVerificationStale = (values: any) => {
    if (!lastVerifiedValues) return true
    return (
      lastVerifiedValues.checkDate !== values.checkDate ||
      lastVerifiedValues.checkNumber !== values.checkNumber ||
      lastVerifiedValues.checkAmount !== values.checkAmount ||
      lastVerifiedValues.defaultPayer !== values.defaultPayer
    )
  }

  const isPaidInFull = paymentDetails?.paidInFull === true
  const [verifyLoading, setVerifyLoading] = useState(false)

  return (
    <Dialog
      fullWidth={true}
      maxWidth="xl"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px', overflow: 'hidden' }}>
        {isPaidInFull && (
          <Typography
            color="error"
            style={{ marginBottom: 16, textAlign: 'center', fontWeight: 600 }}
          >
            This case is paid in full. No further payments can be applied.
          </Typography>
        )}
        {(loading || caseLoading || verifyLoading) && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={Yup.object().shape({
            checkDate: Yup.date()
              .min(new Date('2000-01-01'), 'Date must be on or after January 1, 2000')
              .test('is-valid-date', 'Date must be a valid date', () => {
                const dateInput = document.getElementById(
                  'checkDate'
                ) as HTMLInputElement | null

                if (dateInput && dateInput.value === '') {
                  if (dateInput.validity.badInput) {
                    return false
                  }
                } else {
                  return true
                }

                return true
              })
              .test(
                'check-date-required-with-number',
                'Date of Check is required',
                function (value) {
                  return value !== null && value !== undefined
                }
              ),
            selectedLineItemId: Yup.string().required('Line item is required'),
            checkNumber: Yup.string().required('Check number is required'),
            defaultPayer: Yup.string().required('Default payer is required'),
            checkAmount: Yup.number()
              .required('Check amount is required')
              .positive('Check amount must be a positive number'),
            AmountToApply: Yup.number()
              .required('Amount to apply is required')
              .positive('Amount to apply must be a positive number')
              .test(
                'max-balance',
                'Amount to apply cannot be greater than the balance due',
                function (value) {
                  const balanceDue = paymentDetails?.remainingBalance
                  if (
                    value === undefined ||
                    value === null ||
                    balanceDue === undefined ||
                    balanceDue === null
                  )
                    return true
                  return Number(value) <= Number(balanceDue)
                }
              ),
            comments: Yup.string().when('requireComment', {
              is: false,
              then: (schema: any) => schema.notRequired(),
              otherwise: (schema: any) => schema.required('Comments are required'),
            }),
          })}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={processApplyPayment}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            isValid,
            handleBlur,
            setFieldValue,
          }) => (
            <>
              <Form>
                <fieldset
                  disabled={isPaidInFull || allLineItemsDisabled}
                  style={{ border: 0, padding: 0, margin: 0 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Autocomplete
                        options={selectItems}
                        getOptionLabel={(item: any) => item.description}
                        value={
                          selectItems.find((item) => item.id === selectedLineItemId) ||
                          null
                        }
                        onChange={(_, newValue: any) => {
                          setSelectedLineItemId(newValue ? newValue.id : '')
                          setFieldValue('lineItemSelect', newValue ? newValue.id : '')
                        }}
                        disabled={isPaidInFull || allLineItemsDisabled}
                        size="small"
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Line Item"
                            variant="outlined"
                            required
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                      {invoiceError && (
                        <Typography
                          color="error"
                          style={{
                            textAlign: 'left',
                            fontWeight: 'bold',
                          }}
                        >
                          <em>{invoiceError}</em>
                        </Typography>
                      )}
                    </Grid>
                    <Grid
                      container
                      item
                      direction="row"
                      spacing={2}
                      style={{ paddingTop: '9px', paddingBottom: '5px' }}
                    >
                      <Typography
                        variant={'body1'}
                        color="textSecondary"
                        style={{
                          height: '18px',
                          paddingLeft: '9px',
                          marginTop: '8px',
                          marginBottom: '4px',
                        }}
                      >
                        INVOICE SUMMARY
                      </Typography>
                    </Grid>
                    <Grid container item direction="row" spacing={2}>
                      <Grid
                        container
                        item
                        direction="row"
                        spacing={2}
                        style={{ paddingBottom: '20px' }}
                      >
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>Case #:&nbsp;&nbsp;</span>
                          <span>
                            {paymentDetails?.caseNumber !== undefined &&
                            paymentDetails?.caseNumber !== null
                              ? paymentDetails.caseNumber
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>Group #:&nbsp;&nbsp;</span>
                          <span>
                            {paymentDetails?.groupNumber !== undefined &&
                            paymentDetails?.groupNumber !== null
                              ? paymentDetails.groupNumber
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            Patient Name:&nbsp;&nbsp;
                          </span>
                          <span>
                            {paymentDetails?.patientName !== undefined &&
                            paymentDetails?.patientName !== null
                              ? paymentDetails.patientName
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>Payer:&nbsp;&nbsp;</span>
                          <span>
                            {paymentDetails?.payments &&
                            paymentDetails.payments.length > 0
                              ? paymentDetails.payments[0].payer || '[UNKNOWN]'
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            Total Invoiced:&nbsp;&nbsp;
                          </span>
                          <span>
                            {paymentDetails?.totalAmount !== undefined &&
                            paymentDetails?.totalAmount !== null
                              ? formatCurrency(paymentDetails.totalAmount)
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            Amount Paid:&nbsp;&nbsp;
                          </span>
                          <span>
                            {paymentDetails?.totalAmount !== undefined &&
                            paymentDetails?.totalAmount !== null
                              ? formatCurrency(paymentDetails.totalAmount)
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                        <Grid item style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            Balance Due:&nbsp;&nbsp;
                          </span>
                          <span>
                            {paymentDetails?.remainingBalance !== undefined &&
                            paymentDetails?.remainingBalance !== null
                              ? formatCurrency(paymentDetails.remainingBalance)
                              : '[UNKNOWN]'}
                          </span>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid xs={12} container item direction="row" spacing={2}>
                    <Grid xs={2} item style={{ whiteSpace: 'nowrap' }}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'checkDate' }}
                        InputLabelProps={{ shrink: true }}
                        type="date"
                        name="checkDate"
                        variant="outlined"
                        required={true}
                        onChange={(e) => {
                          handleChange(e)
                          setIsCheckVerified(false)
                          setVerifyResponse(null)
                        }}
                        label="Check Date"
                        fullWidth
                        error={!!(touched.checkDate && errors.checkDate)}
                        helperText={(touched.checkDate && errors.checkDate) as string}
                        FormHelperTextProps={{
                          style: {
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            maxWidth: '240px',
                          },
                        }}
                        disabled={!selectedLineItemId}
                      />
                    </Grid>
                    <Grid xs={2} item style={{ whiteSpace: 'nowrap' }}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'checkNumber', maxLength: 15 }}
                        InputLabelProps={{ shrink: true }}
                        required={true}
                        name="checkNumber"
                        variant="outlined"
                        onChange={handleChange}
                        label="Check Number"
                        fullWidth
                        error={!!(touched.checkNumber && errors.checkNumber)}
                        helperText={(touched.checkNumber && errors.checkNumber) as string}
                        FormHelperTextProps={{
                          style: {
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            maxWidth: '240px',
                          },
                        }}
                        disabled={!selectedLineItemId}
                      />
                    </Grid>
                    <Grid xs={2} item style={{ whiteSpace: 'nowrap' }}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'defaultPayer' }}
                        InputLabelProps={{ shrink: true }}
                        name="defaultPayer"
                        variant="outlined"
                        required={true}
                        onChange={handleChange}
                        label="Payer"
                        fullWidth
                        value={values.defaultPayer}
                        error={!!(touched.defaultPayer && errors.defaultPayer)}
                        helperText={
                          (touched.defaultPayer && errors.defaultPayer) as string
                        }
                        FormHelperTextProps={{
                          style: {
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            maxWidth: '240px',
                          },
                        }}
                        disabled={!selectedLineItemId}
                      />
                    </Grid>
                    <Grid xs={2} item style={{ whiteSpace: 'nowrap' }}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'checkAmount' }}
                        InputLabelProps={{ shrink: true }}
                        name="checkAmount"
                        variant="outlined"
                        required={true}
                        onChange={handleChange}
                        label="Check Amount"
                        fullWidth
                        error={!!(touched.checkAmount && errors.checkAmount)}
                        helperText={(touched.checkAmount && errors.checkAmount) as string}
                        FormHelperTextProps={{
                          style: {
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            maxWidth: '240px',
                          },
                        }}
                        disabled={!selectedLineItemId}
                      />
                    </Grid>
                    <Grid xs={2} item style={{ whiteSpace: 'nowrap' }}>
                      <TextField
                        size="small"
                        onBlur={handleBlur}
                        inputProps={{ id: 'AmountToApply' }}
                        InputLabelProps={{ shrink: true }}
                        required={true}
                        name="AmountToApply"
                        variant="outlined"
                        onChange={handleChange}
                        label="Amount To Apply"
                        fullWidth
                        error={!!(touched.AmountToApply && errors.AmountToApply)}
                        helperText={
                          (touched.AmountToApply && errors.AmountToApply) as string
                        }
                        FormHelperTextProps={{
                          style: {
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            maxWidth: '240px',
                          },
                        }}
                        disabled={!selectedLineItemId}
                      />
                    </Grid>
                    <Grid
                      item
                      style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 15 }}
                    >
                      <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        disabled={!isValid}
                        onClick={async () => {
                          setVerifyLoading(true)
                          try {
                            const response = await verifyReceivedCheck({
                              checkId: values.checkId ?? '',
                              checkNumber: values.checkNumber ?? '',
                              checkDate: values.checkDate ?? '',
                              checkAmount: Number(values.checkAmount) || 0,
                              payer: values.defaultPayer ?? '',
                              totalFundsApplied: Number(values.totalFundsApplied) || 0,
                              totalRemainingFunds:
                                Number(values.totalRemainingFunds) || 0,
                              refund: values.refund || false,
                              validateCheckResponse: '',
                              applyPaymentResponse: '',
                            })
                            setVerifyResponse(response?.validateCheckResponse || null)
                            setIsCheckVerified(true)
                            setLastVerifiedValues({
                              checkNumber: values.checkNumber,
                              checkDate: values.checkDate,
                              checkAmount: values.checkAmount,
                              defaultPayer: values.defaultPayer,
                            })
                            if (response?.totalRemainingFunds !== undefined) {
                              setFieldValue(
                                'totalRemainingFunds',
                                response.totalRemainingFunds
                              )
                            }
                          } finally {
                            setVerifyLoading(false)
                          }
                        }}
                      >
                        Verify Check
                      </Button>
                    </Grid>
                    {verifyResponse && (
                      <Grid
                        item
                        xs={12}
                        container
                        justifyContent="flex-end"
                        style={{ marginTop: 12 }}
                      >
                        <Typography
                          style={{
                            color: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          variant="subtitle1"
                        >
                          {Number(values.totalRemainingFunds) >=
                          Number(values.AmountToApply) ? (
                            <span
                              style={{ color: 'green', marginLeft: 8, paddingTop: '8px' }}
                              title="Check has remaining funds"
                            >
                              <CheckIcon></CheckIcon>&nbsp;&nbsp;
                            </span>
                          ) : (
                            <span
                              style={{ color: 'red', marginLeft: 8, paddingTop: '8px' }}
                              title="No remaining funds"
                            >
                              <ClearIcon></ClearIcon>
                              &nbsp;&nbsp;
                            </span>
                          )}
                          {verifyResponse}{' '}
                        </Typography>
                      </Grid>
                    )}
                    <Grid
                      container
                      item
                      direction="row"
                      spacing={2}
                      style={{ paddingLeft: 10 }}
                    >
                      <Grid item xs={12}>
                        <Accordion style={{ marginBottom: '10px' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                              color="textSecondary"
                              variant="body2"
                              style={{ width: '100%' }}
                            >
                              PAST RECEIVED CHECKS
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Table size="small" style={{ width: '100%' }}>
                              <TableHead>
                                <TableRow className="gridPadding">
                                  <TableCell align="right">Check Date</TableCell>
                                  <TableCell>Check Number</TableCell>
                                  <TableCell>Payer</TableCell>
                                  <TableCell align="right">Applied Date</TableCell>
                                  <TableCell>Applied By</TableCell>
                                  <TableCell align="right">Amount Paid</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {appliedPayments && appliedPayments.length > 0 ? (
                                  appliedPayments.map((record, index) => (
                                    <TableRow className="gridPadding" key={index}>
                                      <TableCell align="right">
                                        {formatDate(record.checkDate)}
                                      </TableCell>
                                      <TableCell>{record.checkNumber}</TableCell>
                                      <TableCell>{record.payer}</TableCell>
                                      <TableCell align="right">
                                        {formatDate(record.appliedDatePaid)}
                                      </TableCell>
                                      <TableCell>{record.appliedBy}</TableCell>
                                      <TableCell align="right">
                                        {record.amountPaid
                                          ? formatCurrency(record.amountPaid)
                                          : ''}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow key="blank">
                                    <TableCell colSpan={6} align="center">
                                      <i>No records found</i>
                                    </TableCell>
                                  </TableRow>
                                )}
                                <TableRow key="footer">
                                  <TableCell colSpan={4}>
                                    <div />
                                  </TableCell>
                                  <TableCell align="right">
                                    <strong>Total:</strong>
                                  </TableCell>
                                  <TableCell style={{ width: '8%' }} align="right">
                                    {appliedPayments && appliedPayments.length > 0 ? (
                                      <span>
                                        {formatCurrency(
                                          appliedPayments
                                            .map((x) => Number(x.amountPaid) || 0)
                                            .reduce((a, b) => a + b, 0)
                                        )}
                                      </span>
                                    ) : (
                                      <span>[UNKNOWN]</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>{' '}
                          </AccordionDetails>{' '}
                        </Accordion>
                      </Grid>
                    </Grid>
                    {props.action.displayComment && (
                      <Grid item xs={12}>
                        <TextField
                          onBlur={handleBlur}
                          inputProps={{ id: 'comments' }}
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          name="comments"
                          variant="outlined"
                          value={values.comments}
                          onChange={handleChange}
                          label="Comments"
                          fullWidth
                          multiline
                          minRows={4}
                          required={props.action.requireComment}
                          error={!!(touched.comments && errors.comments)}
                          helperText={(touched.comments && errors.comments) as string}
                        />
                      </Grid>
                    )}
                  </Grid>
                </fieldset>
                <DialogActions>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={
                      loading ||
                      !isValid ||
                      !isCheckVerified ||
                      isVerificationStale(values) ||
                      !values.AmountToApply ||
                      !!errors.AmountToApply ||
                      (paymentDetails?.remainingBalance !== undefined &&
                        Number(values.AmountToApply) >
                          Number(values.totalRemainingFunds)) ||
                      !!invoiceError
                    }
                  >
                    Execute
                  </Button>
                  <Button onClick={handlesOnClose} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Form>
            </>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ApplyPaymentModal
